import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InventoryAlerts = ({ lowStockItems, reorderSuggestions, analytics }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount || 0);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US')?.format(number || 0);
  };

  const formatPercentage = (percentage) => {
    const value = percentage || 0;
    const sign = value > 0 ? '+' : '';
    return `${sign}${value?.toFixed(1)}%`;
  };

  const getUrgencyColor = (stock, threshold) => {
    const ratio = stock / (threshold || 1);
    if (ratio <= 0.2) return 'text-error';
    if (ratio <= 0.5) return 'text-warning';
    return 'text-muted-foreground';
  };

  const getUrgencyBg = (stock, threshold) => {
    const ratio = stock / (threshold || 1);
    if (ratio <= 0.2) return 'bg-error/10 border-error/20';
    if (ratio <= 0.5) return 'bg-warning/10 border-warning/20';
    return 'bg-muted/10 border-border';
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return 'text-success';
    if (growth < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  return (
    <div className="space-y-6">
      {/* Low Stock Alerts */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={20} className="text-warning" />
              <h3 className="font-medium text-foreground">Low Stock Alerts</h3>
            </div>
            <span className="text-sm text-muted-foreground">
              {lowStockItems?.length || 0} item{(lowStockItems?.length || 0) !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {lowStockItems?.length > 0 ? (
            <div className="divide-y divide-border">
              {lowStockItems?.map((item) => (
                <div key={item?.id} className={`p-4 border-l-4 ${getUrgencyBg(item?.stock, item?.lowStockThreshold)}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-foreground">{item?.name}</h4>
                        <span className="text-xs text-muted-foreground">({item?.sku})</span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className={`font-medium ${getUrgencyColor(item?.stock, item?.lowStockThreshold)}`}>
                          {item?.stock || 0} left
                        </span>
                        <span className="text-muted-foreground">
                          Threshold: {item?.lowStockThreshold || 10}
                        </span>
                        <span className="text-muted-foreground">
                          Category: {item?.category || 'N/A'}
                        </span>
                      </div>
                      {item?.lastSold && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          Last sold: {new Date(item?.lastSold)?.toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <Button variant="outline" size="sm">
                        <Icon name="Plus" size={14} className="mr-1" />
                        Restock
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Icon name="Edit" size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
              <h4 className="font-medium text-foreground mb-1">All items well stocked</h4>
              <p className="text-sm text-muted-foreground">No low stock alerts at this time.</p>
            </div>
          )}
        </div>
      </div>

      {/* Reorder Suggestions */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="TrendingUp" size={20} className="text-primary" />
              <h3 className="font-medium text-foreground">AI Reorder Suggestions</h3>
            </div>
            <span className="text-sm text-muted-foreground">
              {reorderSuggestions?.length || 0} suggestion{(reorderSuggestions?.length || 0) !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {reorderSuggestions?.length > 0 ? (
            <div className="divide-y divide-border">
              {reorderSuggestions?.map((suggestion) => (
                <div key={suggestion?.id} className="p-4 hover:bg-muted/20 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-foreground">{suggestion?.name}</h4>
                        <span className="text-xs text-muted-foreground">({suggestion?.sku})</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm mb-2">
                        <div>
                          <span className="text-muted-foreground">Suggested Qty:</span>
                          <span className="ml-2 font-medium text-foreground">{suggestion?.suggestedQuantity || 0}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Est. Cost:</span>
                          <span className="ml-2 font-medium text-foreground">
                            {formatCurrency(suggestion?.estimatedCost)}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Lead Time:</span>
                          <span className="ml-2 font-medium text-foreground">{suggestion?.leadTime || 0} days</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Confidence:</span>
                          <span className="ml-2 font-medium text-primary">{suggestion?.confidence || 0}%</span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <Icon name="Brain" size={12} className="inline mr-1" />
                        {suggestion?.reason || 'AI-generated reorder suggestion'}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <Button variant="outline" size="sm">
                        <Icon name="ShoppingCart" size={14} className="mr-1" />
                        Create PO
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Icon name="X" size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Icon name="Brain" size={48} className="text-muted-foreground mx-auto mb-3" />
              <h4 className="font-medium text-foreground mb-1">No suggestions available</h4>
              <p className="text-sm text-muted-foreground">AI will analyze your inventory patterns and provide recommendations.</p>
            </div>
          )}
        </div>
      </div>

      {/* Live Analytics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Package" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Total Items</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatNumber(analytics?.totalItems)}</p>
          <p className={`text-xs ${getGrowthColor(analytics?.monthlyGrowth)}`}>
            {formatPercentage(analytics?.monthlyGrowth)} this month
          </p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="DollarSign" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Inventory Value</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(analytics?.totalValue)}</p>
          <p className="text-xs text-muted-foreground">
            {formatNumber(analytics?.currentMonthItems)} items added this month
          </p>
        </div>
      </div>

      {/* Additional Analytics Cards */}
      <div className="grid grid-cols-1 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-foreground">Inventory Health</h4>
            <Icon name="Activity" size={16} className="text-primary" />
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">In Stock</span>
              <span className="text-sm font-medium text-success">
                {formatNumber(analytics?.totalItems - (lowStockItems?.length || 0))}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Low Stock</span>
              <span className="text-sm font-medium text-warning">
                {formatNumber(lowStockItems?.length)}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Reorder Needed</span>
              <span className="text-sm font-medium text-error">
                {formatNumber(reorderSuggestions?.length)}
              </span>
            </div>
          </div>
          
          {/* Progress bar for stock levels */}
          <div className="mt-4">
            <div className="w-full bg-muted/20 rounded-full h-2">
              <div 
                className="bg-success h-2 rounded-full" 
                style={{ 
                  width: `${analytics?.totalItems > 0 
                    ? ((analytics?.totalItems - (lowStockItems?.length || 0)) / analytics?.totalItems) * 100 
                    : 0}%` 
                }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics?.totalItems > 0 
                ? Math.round(((analytics?.totalItems - (lowStockItems?.length || 0)) / analytics?.totalItems) * 100)
                : 0}% of items well stocked
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryAlerts;